import { Fragment, useMemo, type HTMLAttributes, type ReactNode } from 'react'
import { marked } from 'marked'
import styles from './RichTextContent.module.css'

export interface RichTextContentProps extends HTMLAttributes<HTMLDivElement> {
  /** Markdown string to render */
  markdown: string
  /**
   * Renders a block in place of a whole line, e.g. a live card for a Carousel
   * link. Called with each trimmed line outside code fences; return nothing to
   * leave the line as markdown.
   */
  embed?: (line: string) => ReactNode | null | undefined
}

type Block = { kind: 'markdown'; html: string } | { kind: 'embed'; node: ReactNode }

function toHtml(markdown: string): string {
  return marked.parse(markdown, { async: false }) as string
}

/** Splits markdown around lines the embed callback claims. */
function toBlocks(markdown: string, embed: NonNullable<RichTextContentProps['embed']>): Block[] {
  const blocks: Block[] = []
  let buffer: string[] = []
  let inFence = false

  const flush = () => {
    const text = buffer.join('\n')
    if (text.trim()) blocks.push({ kind: 'markdown', html: toHtml(text) })
    buffer = []
  }

  for (const line of markdown.split('\n')) {
    const trimmed = line.trim()
    if (trimmed.startsWith('```') || trimmed.startsWith('~~~')) inFence = !inFence
    const node = !inFence && trimmed ? embed(trimmed) : null
    if (node) {
      flush()
      blocks.push({ kind: 'embed', node })
    } else {
      buffer.push(line)
    }
  }
  flush()
  return blocks
}

export function RichTextContent({ markdown, embed, className, ...props }: RichTextContentProps) {
  const blocks = useMemo(() => {
    if (!markdown) return null
    if (!embed) return toHtml(markdown)
    return toBlocks(markdown, embed)
  }, [markdown, embed])

  const classes = [styles.content, className].filter(Boolean).join(' ')

  if (blocks === null || typeof blocks === 'string') {
    return <div className={classes} dangerouslySetInnerHTML={{ __html: blocks ?? '' }} {...props} />
  }

  return (
    <div className={classes} {...props}>
      {blocks.map((b, i) => (
        <Fragment key={i}>
          {b.kind === 'markdown'
            ? <div className={styles.chunk} dangerouslySetInnerHTML={{ __html: b.html }} />
            : b.node}
        </Fragment>
      ))}
    </div>
  )
}
